/**
 * 右上角**全局归属**（品牌 / 站点）的**纯函数**：选项生成、默认值收敛、URL/本地持久化编码。
 *
 * 依据 `docs/project-brief.md` §9 R12（右上角常驻 品牌 / 站点）与
 * `docs/frontend/ux-patterns.md` §9 第 4 条（全局归属 → 右上角常驻；
 * 时间范围等页面上下文 → 数据区左上角，两者不混放）。
 *
 * ## 为什么这些逻辑要单独拿出来
 *
 * 它们是「默认值到底是谁」的判定：单品牌默认选它、店铺默认「全部店铺」、
 * 存的值在品牌变动后是否还有效。这些必须在 node 环境里逐条断言，
 * 而不是靠"点一下看看"——默认值错了会让用户以为自己在看 A 站点，其实在看全部站点。
 *
 * ## 三个空值语义（**都是合法的业务值，不是"缺数据"**）
 *
 *  · `brandId === ""` → **全部品牌**（多品牌时的默认）
 *  · `shopId === ""`  → **全部站点**（用户指定的默认值）
 *  · 店铺 `brandId === null` → 该店**尚未归属任何品牌**；它在「全部品牌」下可见，
 *    在任一具体品牌下不可见（不猜归属）。
 */

/** 数据源的行类型（结构化子集，避免 lib → lib 的循环依赖）。 */
export interface BrandOptionSource {
  readonly brandId: string;
  readonly name: string;
  readonly sortOrder: number;
  readonly status: string;
}

export interface ShopOptionSource {
  readonly shopId: string;
  readonly domain: string;
  readonly displayName: string | null;
  readonly brandId: string | null;
}

/** 「全部」哨兵值：与 URL/本地存储里的"未选"共用。 */
export const ALL_BRANDS = "";
export const ALL_SHOPS = "";

/** 在 `<option>` / chip 上显示的名字：显示名优先，其次 domain，最后 id。 */
export function shopLabel(shop: ShopOptionSource): string {
  const display = shop.displayName?.trim() ?? "";
  if (display !== "") return display;
  const domain = shop.domain.trim();
  return domain !== "" ? domain : shop.shopId;
}

/** 品牌下拉的选项：**归档品牌不出现在选择器里**（选了又能筛什么？），按 sortOrder 再 brandId 稳定排序。 */
export function brandOptions(brands: readonly BrandOptionSource[]): readonly BrandOptionSource[] {
  return [...brands]
    .filter((brand) => brand.status !== "archived")
    .sort((left, right) => {
      if (left.sortOrder !== right.sortOrder) return left.sortOrder - right.sortOrder;
      return left.brandId < right.brandId ? -1 : left.brandId > right.brandId ? 1 : 0;
    });
}

/**
 * 按品牌过滤店铺。`brandId === ALL_BRANDS` 时返回全部店铺。
 *
 * ⚠️ **顺序的唯一来源 = 服务端返回顺序**（task-27）：这里**只过滤、不排序**。
 *
 * 为什么去掉旧的 `sort(by shopId)`（2026-10-06 起的实现）：
 *  · 用户要求「设置页能调店铺顺序，右上角切换器按**同一顺序**」⇒ 客户端再排一次
 *    会变成「两处各自排序」⇒ **后端 `shops.sort_order` 怎么调都不会在切换器生效**（必然漂移）；
 *  · 排序（含并列时 `shop_id` 稳定排序）由**服务端**做一次；前端只做品牌过滤，
 *    过滤天然保持相对顺序（§"同源"纪律：顺序/口径只有一个来源）；
 *  · 后端排序字段未就绪时，这里就是"服务端顺序原样渲染"——就绪后**无需再改前端**。
 */
export function shopsForBrand(
  shops: readonly ShopOptionSource[],
  brandId: string,
): readonly ShopOptionSource[] {
  return brandId === ALL_BRANDS ? [...shops] : shops.filter((shop) => shop.brandId === brandId);
}

/**
 * 品牌默认值收敛：
 *  1. 已存值仍然是一个可选品牌 → 保留（用户的显式选择优先）；
 *  2. 否则**只有一个可选品牌 → 默认选它**（用户口径）；
 *  3. 否则 → 全部品牌。
 */
export function resolveBrandDefault(
  brands: readonly BrandOptionSource[],
  storedBrandId: string | null,
): string {
  const options = brandOptions(brands);
  if (storedBrandId !== null && storedBrandId !== ALL_BRANDS) {
    if (options.some((brand) => brand.brandId === storedBrandId)) return storedBrandId;
  }
  return options.length === 1 ? options[0]!.brandId : ALL_BRANDS;
}

/**
 * 店铺默认值收敛：已存值仍属于当前品牌 → 保留；否则**一律回落「全部店铺」**
 * （用户口径的默认值；绝不"猜一个店"悄悄换个口径）。
 */
export function resolveShopDefault(
  shops: readonly ShopOptionSource[],
  brandId: string,
  storedShopId: string | null,
): string {
  if (storedShopId === null || storedShopId === ALL_SHOPS) return ALL_SHOPS;
  return shopsForBrand(shops, brandId).some((shop) => shop.shopId === storedShopId)
    ? storedShopId
    : ALL_SHOPS;
}
